import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  AxiosError,
  InternalAxiosRequestConfig,
  AxiosHeaders,
} from 'axios';
import router from '../router';
import { API_CONFIG } from './api-config';
import { useTopbarStore } from '@/store/topbar';
import { pushFaroLog, pushFaroLogError } from '@/utils/faro';

type TopbarStore = ReturnType<typeof useTopbarStore>;
let cachedTopbarStore: TopbarStore | null = null;
const getTopbarStore = (): TopbarStore => {
  if (!cachedTopbarStore) {
    cachedTopbarStore = useTopbarStore();
  }
  return cachedTopbarStore;
};

export type ApiRequestConfig = {
  skipAuth?: boolean;
  headers?: AxiosRequestConfig['headers'];
  params?: AxiosRequestConfig['params'];
  responseType?: AxiosRequestConfig['responseType'];
  timeout?: number;
};

class HttpError extends Error {
  status: number;
  data?: unknown;
  constructor(message: string, status: number, data?: unknown) {
    super(message);
    this.name = 'HttpError';
    this.status = status;
    this.data = data;
  }
}

// Create a single axios instance
const http: AxiosInstance = axios.create({
  baseURL: API_CONFIG.BASE_URL,
  headers: {
    'Content-Type': API_CONFIG.CONTENT_TYPE_JSON,
  },
});

/**
 * REQUEST INTERCEPTOR
 * - Attach Authorization header if token exists
 */
http.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    const requestConfig = config as InternalAxiosRequestConfig & { skipAuth?: boolean };
    const skipAuth = Boolean(requestConfig.skipAuth);
    const topbarStore = getTopbarStore();
    const token = skipAuth ? '' : await topbarStore.getAccessToken();

    // Do not trigger login redirects from the interceptor.
    // Only attach auth when a usable token is already present.
    if (!skipAuth && token) {
      config.headers = new AxiosHeaders(config.headers);
      config.headers.set(API_CONFIG.AUTH_HEADER, `Bearer ${token}`);
    }
    return config;
  },
  (error: AxiosError) => {
    pushFaroLogError('API request interceptor error', {
      code: error.code,
      method: error.config?.method,
      url: error.config?.url,
      baseUrl: error.config?.baseURL,
    });
    return Promise.reject(error);
  }
);

const SAFE_ERROR_KEYS = ['error', 'errorCode', 'code', 'message', 'type', 'reason'] as const;

function extractSafeErrorDetails(responseBody: unknown): Record<string, unknown> {
  if (!responseBody || typeof responseBody !== 'object' || Array.isArray(responseBody)) {
    return {};
  }
  const body = responseBody as Record<string, unknown>;
  return SAFE_ERROR_KEYS.reduce<Record<string, unknown>>((safeDetails, key) => {
    if (key in body) {
      safeDetails[key] = body[key];
    }
    return safeDetails;
  }, {});
}

/**
 * RESPONSE INTERCEPTOR
 * - On error, redirect to global error page with status code
 * - Wrap and rethrow as HttpError for local handling if needed
 */
http.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: AxiosError) => {
    const requestMethod = error.config?.method;
    const requestUrl = error.config?.url;
    const requestBaseUrl = error.config?.baseURL;

    // Network/CORS or no response
    if (!error.response) {
      console.error('API network error', {
        code: error.code,
        method: requestMethod,
        url: requestUrl,
      });
      pushFaroLogError('API network error', {
        code: error.code,
        method: requestMethod,
        url: requestUrl,
        baseUrl: requestBaseUrl,
      });
      return Promise.reject(new HttpError('Network error', 0));
    }

    const { status, data: responseBody } = error.response;
    const safeErrorDetails = extractSafeErrorDetails(responseBody);

    // Full response visible locally in DevTools — not sent to Faro
    console.error(`API response error ${status}`, {
      method: requestMethod,
      url: requestUrl,
      response: responseBody,
    });

    pushFaroLog('API response error', {
      status,
      code: error.code,
      method: requestMethod,
      url: requestUrl,
      baseUrl: requestBaseUrl,
      ...safeErrorDetails,
      responseBody,
    });

    pushFaroLogError(`API response error ${status}`, {
      status,
      code: error.code,
      method: requestMethod,
      url: requestUrl,
      baseUrl: requestBaseUrl,
      ...safeErrorDetails,
      responseBody,
    });

    if (status === 401) {
      await router.push({ name: 'login' });
    }

    return Promise.reject(new HttpError(`HTTP ${status}`, status, responseBody));
  }
);

/**
 * Helper to return response.data with typing
 */
async function handleResponse<T>(promise: Promise<AxiosResponse<T>>): Promise<T> {
  const resp = await promise;
  return resp.data;
}

// API with get, post, put, patch, delete
export const api = {
  get<T = unknown>(url: string, config?: ApiRequestConfig) {
    return handleResponse<T>(http.get<T>(url, config));
  },
  post<T = unknown>(url: string, body?: unknown, config?: ApiRequestConfig) {
    return handleResponse<T>(http.post<T>(url, body, config));
  },
  put<T = unknown>(url: string, body?: unknown, config?: ApiRequestConfig) {
    return handleResponse<T>(http.put<T>(url, body, config));
  },
  patch<T = unknown>(url: string, body?: unknown, config?: ApiRequestConfig) {
    return handleResponse<T>(http.patch<T>(url, body, config));
  },
  delete<T = unknown>(url: string, config?: ApiRequestConfig) {
    return handleResponse<T>(http.delete<T>(url, config));
  },
};

export { http, HttpError };
